/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { memo, useCallback, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import debounce from "lodash.debounce";
import { EditorProvider } from "@tiptap/react";
import { tiptapExtensions } from "cms/rich-text/simplicity-composer-tiptap/tiptap-extensions";
import { useThrottledUpdate } from "./use-throttled-update";
import { Toolbar } from "cms/rich-text/simplicity-composer-tiptap/toolbar/toolbar";
import { LinkBubbleMenu } from "cms/rich-text/simplicity-composer-tiptap/tiptap-extensions/simplicity-link/link-bubble-menu";
import { DocumentBubbleMenu } from "cms/rich-text/simplicity-composer-tiptap/document/document-bubble-menu";
import { ImageBubbleMenu } from "cms/rich-text/simplicity-composer-tiptap/image/image-bubble-menu";

import styles from "./simplicity-composer-tiptap.module.css";

/** @typedef {import("@tiptap/react").JSONContent} Content */
/**
 * @param {object} props
 * @param {(content: Content) => void} props.onChange - Is frozen the initial render
 * @param {number | undefined} props.debounceFor
 * @param {Content | undefined} props.richtext
 */
function _SimplicityComposerTipTap (props) {
    
    const onChange = props?.onChange;
    const className = props?.className;
    const richtext = props?.richtext;
    const debounceFor = props?.debounceFor ?? 600;
    const enableQuoteDefault = props?.enableQuoteDefault ?? false;
    
    const [error, setError] = useState();
    
    const update = useThrottledUpdate(190);

    /**
     * @param {import("@tiptap/react").EditorEvents['update']} change
     */
    function onEditorChange (change) {
        const { editor } = change;
        const richText = editor.getJSON();
        onChange(richText);
    }
    
    /* This is wired in the transaction listener to process updates in a debounced manner */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onUpdate = useCallback(
        debounce(onEditorChange, debounceFor),
        [debounceFor]
    );
    
    const onContentError = (props) => {
        const error = props?.error;
        // const editor = props?.editor;
        // const disableCollaboration = props?.disableCollaboration;
        console.error(error);
        console.warn("Rich Text error");
        console.warn(`Offending content: ${JSON.stringify(richtext,null,2)}`);
        setError(error);
    };
    
    const onCreate = (props) => {
        const { editor } = props;
        if (editor.options.onTransaction) {
            // remove default transaction listener
            editor.off("transaction");
        }
        // set throttled listener
        editor.on("transaction", update);

        // Set Quote to be enabled by default and also check if the editor has no content.
        const text = editor.getText().trim();
        if (enableQuoteDefault && !text) {
            editor.chain().focus().toggleBlockquote().run();
        }

    };
    if (error) {
        return null;
    }
    
    return (
        <div className={`rich-text-container ${className}`} style={{ width: "100%" }}>
            <ErrorBoundary
                fallback={
                    <details className="richtext-error">
                        <summary>ERROR: Failed to read rich text content: </summary>
                        <pre className="richtext_debug">{JSON.stringify(richtext, null, 2)}</pre>
                    </details>
                }
            >
                <EditorProvider
                    enableContentCheck={true}
                    slotBefore={<Toolbar/>}
                    onContentError={onContentError}
                    extensions={tiptapExtensions}
                    onCreate={onCreate}
                    content={richtext}
                    onUpdate={onUpdate}
                    editorProps={{
                        attributes: {
                            class: styles.editor,
                            spellCheck: true
                        },
                    }}
                >
                    <LinkBubbleMenu />
                    <DocumentBubbleMenu />
                    <ImageBubbleMenu />
                </EditorProvider>
            </ErrorBoundary>
        </div>
    );
}

// performance hack that prevents the editor from rendering more than once
export const SimplicityComposerTipTap = memo(_SimplicityComposerTipTap, () => true);
SimplicityComposerTipTap.displayName = "SimplicityComposerTipTap";
